/**
 * <product-gallery> — the progress track beside (or beneath) the product images, and the swap
 * between colours' images.
 *
 * On a phone the gallery is a horizontal swipe, one image per screen, and the gallery scrolls
 * itself; from 1025px it is a column of viewport-height images that the page itself scrolls
 * through, with no scroller of its own (SAVETTE-65: an inner scroller held on to the wheel at the
 * last image). This element reports how far through the images the shopper is: the swipe's
 * scroll position, or how far the page has carried the column past the viewport.
 *
 * On the column the indicator is a short segment travelling a fixed track, which is what the
 * approved frame draws; on the swipe it is a bar that fills.
 *
 * Colour runs (SAVETTE-64): when a product's colours are its variants, sections/product.liquid
 * renders each colour's images as a run, [data-variant-media] carrying the ids of the variants it
 * belongs to, and shows only the selected variant's (the grouping rule is written there). When
 * <product-form> reports a new variant, the matching run is shown and the others hidden and inert,
 * and the gallery goes back to that colour's first image. A variant with no run of its own shows
 * every run, as the server does. Nothing is announced: the colour name under the swatches already
 * says what changed, and the gallery is not a live region.
 *
 * Nothing here animates scrolling. The shopper drives it with a swipe, a wheel or a keyboard, and
 * the reset to the first image is an instant jump, which is why there is no motion to suppress for
 * prefers-reduced-motion beyond the track's own fill.
 *
 * The column's offsets (SAVETTE-65). Two custom properties are written for the column layout:
 * - --product-sticky-top, on the product: the header's laid-out height, which every image's height
 *   and the sticky buy box and indicator measure from. It is measured here, when the header
 *   changes size, rather than read from --header-total: assets/header.js republishes that one
 *   while a drawer is open (down to 0 once a non-sticky header has scrolled away), and the whole
 *   column would resize under the drawer.
 * - --product-buy-top, on the buy box: where it sticks. A box that fits under the header sticks by
 *   its top; a taller one (accordions open on a laptop screen) sticks by its bottom, so the page's
 *   scroll reveals the rest of it instead of leaving it out of reach until the images end.
 */
class ProductGallery extends HTMLElement {
  connectedCallback() {
    this.thumb = this.querySelector('[data-progress-thumb]');
    this.track = this.querySelector('.product__progress');
    this.scroller = this.querySelector('[data-media-scroller]');
    this.runs = [...this.querySelectorAll('[data-variant-media]')];
    this.product = this.closest('.product');
    this.buy = this.product?.querySelector('.product__buy');
    this.header = document.querySelector('site-header');
    // the swipe's scroller attributes, taken off while the images are the page's column (where
    // there is nothing for them to scroll) and put back for the swipe
    this.scrollerAttributes = ['tabindex', 'role', 'aria-label'].map((name) => [
      name,
      this.scroller?.getAttribute(name),
    ]);
    // the column layout's breakpoint in sections/product.liquid
    this.columnQuery = window.matchMedia('(min-width: 1025px)');

    this.resizeObserver = new ResizeObserver(this.scheduleMeasure);
    if (this.header) this.resizeObserver.observe(this.header);
    if (this.buy) this.resizeObserver.observe(this.buy);
    window.addEventListener('resize', this.scheduleMeasure, { passive: true });
    this.measure();

    // the scroller is all the colour swap needs; the progress track is optional, so a missing
    // track can never stop the gallery following the colour
    if (!this.scroller) return;

    this.scroller.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onScroll, { passive: true });
    this.columnQuery.addEventListener('change', this.onLayout);

    // <product-form> is a sibling inside the product, not a child, so listen where both live
    if (this.runs.length > 1) {
      this.eventScope = this.product ?? document;
      this.eventScope.addEventListener('product-form:variant', this.onVariant);
    }
    this.onLayout();
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
    window.removeEventListener('resize', this.scheduleMeasure);
    this.scroller?.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onScroll);
    this.columnQuery?.removeEventListener('change', this.onLayout);
    this.eventScope?.removeEventListener('product-form:variant', this.onVariant);
    if (this.frame) cancelAnimationFrame(this.frame);
    if (this.measureFrame) cancelAnimationFrame(this.measureFrame);
  }

  onScroll = () => {
    if (this.frame) return;
    this.frame = requestAnimationFrame(() => {
      this.frame = null;
      this.update();
    });
  };

  /** Swipe or column: only the column follows the page's scroll, and only the swipe is a region. */
  onLayout = () => {
    const column = this.isColumn();
    if (column) window.addEventListener('scroll', this.onScroll, { passive: true });
    else window.removeEventListener('scroll', this.onScroll);
    for (const [name, value] of this.scrollerAttributes) {
      if (value == null) continue;
      if (column) this.scroller.removeAttribute(name);
      else this.scroller.setAttribute(name, value);
    }
    this.update();
  };

  scheduleMeasure = () => {
    if (this.measureFrame) return;
    this.measureFrame = requestAnimationFrame(() => {
      this.measureFrame = null;
      this.measure();
    });
  };

  /** Publish the column's offsets (above); the phone layout does not read them. */
  measure() {
    const top = this.header?.offsetHeight ?? 0;
    (this.product ?? this).style.setProperty('--product-sticky-top', `${top}px`);
    if (this.buy) {
      const buyTop = Math.min(top, window.innerHeight - this.buy.offsetHeight);
      this.buy.style.setProperty('--product-buy-top', `${buyTop}px`);
    }
    if (this.scroller) this.update();
  }

  /** Show the chosen variant's run of images; an unresolved choice leaves the gallery as it is. */
  onVariant = (event) => {
    const id = event.detail?.variant?.id;
    if (id == null) return;
    const key = String(id);
    const match = this.runs.find((run) => run.dataset.variantMedia.split(/\s+/).includes(key));

    let changed = false;
    for (const run of this.runs) {
      const show = !match || run === match;
      if (run.hidden !== show) continue;
      // the newly shown colour's first image is its first slide: fetch it now rather than wait
      // for the lazy loader to notice it, set before the run is shown so it starts loading at once
      if (show) run.querySelector('img')?.setAttribute('loading', 'eager');
      run.hidden = !show;
      run.inert = !show;
      // a film playing in a colour being put away must not carry on out of sight
      if (!show) run.querySelectorAll('video').forEach((video) => video.pause());
      changed = true;
    }
    // the first variant event comes as the form connects, on the colour the server rendered:
    // nothing changes, so the shopper's place in the gallery is kept
    if (!changed) return;

    this.countVisible();
    this.scroller.scrollLeft = 0;
    // on the column the page is what scrolls: if it has carried the shopper past the first image,
    // bring the column's top back to where it starts, under the header
    if (this.isColumn()) {
      const offset = this.scroller.getBoundingClientRect().top - this.columnTop();
      if (offset < 0) window.scrollBy({ top: offset, behavior: 'instant' });
    }
    this.update();
  };

  /** Whether the gallery is the desktop column, which the page scrolls, rather than the swipe. */
  isColumn() {
    return this.columnQuery.matches;
  }

  /**
   * Where the column's top sits, from the viewport's top, when its first image fills the band
   * under the header: each image is the band's height, so the band starts that far above the
   * viewport's bottom.
   */
  columnTop() {
    if (this.visibleCount == null) this.countVisible();
    return window.innerHeight - (this.firstVisible?.offsetHeight ?? window.innerHeight);
  }

  /** How many images the shopper can reach: those outside the hidden colours' runs. */
  countVisible() {
    const visible = [...this.querySelectorAll('[data-media-item]')].filter((item) => !item.closest('[hidden]'));
    this.visibleCount = visible.length;
    this.firstVisible = visible[0];
  }

  update() {
    if (!this.thumb || !this.track) return;
    if (this.visibleCount == null) this.countVisible();
    // one image (or none) has nothing to report: the track keeps its resting state, as it did
    // before a colour could have a single image
    if (this.visibleCount < 2) {
      this.thumb.style.width = '';
      this.thumb.style.transform = '';
      return;
    }

    // the column: how far the page has carried it from its first image filling the band to its
    // last; the swipe: the scroller's own position
    const vertical = this.isColumn();
    let travel;
    let at;
    if (vertical) {
      const top = this.columnTop();
      const rect = this.scroller.getBoundingClientRect();
      travel = rect.height - (window.innerHeight - top);
      at = top - rect.top;
    } else {
      travel = this.scroller.scrollWidth - this.scroller.clientWidth;
      at = this.scroller.scrollLeft;
    }
    const progress = travel > 0 ? Math.min(Math.max(at / travel, 0), 1) : 0;

    if (vertical) {
      // the column: a short segment travelling a fixed track, as the frame draws it
      const room = this.track.clientHeight - this.thumb.offsetHeight;
      this.thumb.style.transform = `translateY(${(room * progress).toFixed(2)}px)`;
      this.thumb.style.width = '';
    } else {
      // the swipe: the bar fills from the start, and a single image reads as complete rather
      // than as an empty sliver that looks like a rendering fault
      this.thumb.style.width = `${((travel > 0 ? progress : 1) * 100).toFixed(2)}%`;
      this.thumb.style.transform = '';
    }
  }
}

if (!customElements.get('product-gallery')) customElements.define('product-gallery', ProductGallery);
